import { useCallback, useState, type ReactNode } from 'react';
import { SAMPLE_INVESTOR_ID } from '@dealstack/shared/lib/documents/fieldBindingCatalogue';
import { PdfDocumentEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/PdfEditor/PdfDocumentEditor.tsx';
import { useFieldBinding } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FieldBinding/useFieldBinding.ts';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types.ts';
import { useFieldBindingPreview } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useFieldBindingPreview.ts';

interface Props {
	templateId: string;
	file: Blob;
	/** Pinned above the tab bar — the document's own name. */
	panelHeader?: ReactNode;
	/** Folds an unsaved {@link panelHeader} edit into the editor's Save. */
	panelHeaderEdit?: PanelHeaderEdit;
	initialShowStampMetadata: boolean;
	manualPlacementEnabled: boolean;
	postDatingEnabled: boolean;
	showFieldsTab: boolean;
	showSignaturesTab: boolean;
	blockedReason?: string;
	onClose: () => void;
}

/**
 * The deal-scoped data layer for the merged PDF editor.
 *
 * Mirrors `DealFieldBindingEditor`, and for the same reason: the bindings, catalogue and preview
 * queries are only wanted while the editor is actually mounted, so they hang off this rather
 * than off the form that owns the button.
 */
export const DealPdfDocumentEditor = ({
	templateId,
	file,
	panelHeader,
	panelHeaderEdit,
	initialShowStampMetadata,
	manualPlacementEnabled,
	postDatingEnabled,
	showFieldsTab,
	showSignaturesTab,
	blockedReason,
	onClose,
}: Props) => {
	// Mirrors the editor's own preview-investor selection so the catalogue (which is
	// investor-scoped) refetches for whichever investor is currently being previewed.
	const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(SAMPLE_INVESTOR_ID);
	const binding = useFieldBinding(templateId, selectedInvestorId);
	const previewMutation = useFieldBindingPreview();
	const requestPreview = useCallback(
		(investorId: string | null) => {
			if (!investorId) {
				return Promise.reject(new Error('No investor selected for preview'));
			}
			return previewMutation.mutateAsync({ templateId, investorId });
		},
		[templateId, previewMutation]
	);

	return (
		<PdfDocumentEditor
			templateId={templateId}
			file={file}
			binding={binding}
			requestPreview={requestPreview}
			panelHeader={panelHeader}
			panelHeaderEdit={panelHeaderEdit}
			initialShowStampMetadata={initialShowStampMetadata}
			manualPlacementEnabled={manualPlacementEnabled}
			postDatingEnabled={postDatingEnabled}
			showFieldsTab={showFieldsTab}
			showSignaturesTab={showSignaturesTab}
			initialPreviewInvestorId={selectedInvestorId}
			onPreviewInvestorChange={setSelectedInvestorId}
			onClose={onClose}
			{...(blockedReason === undefined ? {} : { blockedReason })}
		/>
	);
};
